/**
 * KsaCostsPanel - one GLOBAL KSA project's purchases and profit.
 *
 * Shawn (CEO, 9/30/26): the PO is in SAR, the goods are bought abroad in USD,
 * ZAR and INR. Each purchase keeps its own currency and the rate used to turn
 * it into the project currency (SAR for KSA): the month's rate from the rate
 * table, or the bank's actual rate typed on the purchase, which always wins.
 * The profit is worked out on the SERVER (lib/ksa.js costSummary) and shown
 * here; the only sum done in the browser is the preview on the form, in exact
 * decimal arithmetic so it shows the cents the server will store.
 *
 * Shown on the KSA project detail (where the main side shows FinancePanel)
 * and as the 'Costs & profit' tab of the KSA opportunity editor.
 * Every URL comes from useApiBase(); every number carries its currency code.
 *
 * Someone else's purchase, an edit of the record or a new exchange rate (r50
 * live events) re-reads it quietly: a re-read that fails keeps the figures.
 */
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useApiBase } from '../../context/DataScope';
import { latestReads, matchKind, matchRecord, recordPart, useLiveRefresh } from '../../live/useLive';
import { fmtDate } from '../../utils/format';
import { INPUT, LABEL, BTN_PRIMARY } from '../forms/formStyles';
import {
  CURRENCIES,
  PURCHASE_STATUSES,
  convertAmount,
  fmtMoney,
  fmtPct,
  fmtRateExact,
  fmtRateFull,
  fxCode,
  fxEffectText,
  isRealDate,
  monthLabel,
  monthOf,
  monthlyRate,
  num,
  parseAmount,
  parseRate,
  purchaseRateSource,
  rateText,
  todayLocal,
} from './ksaMoney';

const GREEN = '#1B5E20';
const RED = '#B71C1C';
const JSON_HEADERS = { 'Content-Type': 'application/json' };

/* ── shared with KsaRatesPanel and the KSA page strip ── */

/** Purchases, rates and totals all move together: the page strip and any open panel re-read on this. */
export const KSA_COSTS_CHANGED = 'globalops:ksa-costs-changed';
export function notifyKsaCostsChanged(from) {
  try {
    window.dispatchEvent(new CustomEvent(KSA_COSTS_CHANGED, { detail: { from } }));
  } catch {
    /* no window */
  }
}

/**
 * fetch + JSON, throwing an Error whose message is fit to show: the server's
 * own `error` text when it sent one, otherwise what failed and the HTTP code.
 */
export async function ksaCall(url, opts, what) {
  let r;
  try {
    r = await fetch(url, opts);
  } catch {
    throw new Error(`${what}: the server could not be reached. Check the connection and try again.`);
  }
  let body = null;
  try {
    body = await r.json();
  } catch {
    /* not JSON */
  }
  if (!r.ok) throw new Error((body && body.error) || `${what} (HTTP ${r.status})`);
  return body;
}

/* ── pure form logic (exported for the tests) ── */

const pick = (o, ...keys) => {
  for (const k of keys) if (o && o[k] != null) return o[k];
  return null;
};
const dateOnly = (d) => (String(d || '').match(/^\d{4}-\d{2}-\d{2}/) || [''])[0];

/** A blank form, or one filled from a saved purchase. */
export function purchaseForm(p) {
  if (!p) {
    return {
      id: null,
      purchase_date: todayLocal(),
      supplier: '',
      description: '',
      amount: '',
      currency: 'USD',
      status: 'Ordered',
      reference: '',
      rateMode: 'monthly',
      rateText: '',
    };
  }
  const amt = num(p.amount);
  return {
    id: p.id,
    purchase_date: dateOnly(p.purchase_date),
    supplier: p.supplier || '',
    description: p.description || '',
    amount: amt == null ? '' : amt.toFixed(2),
    currency: p.currency || 'USD',
    status: p.status || 'Ordered',
    reference: p.reference || '',
    rateMode: p.rate_source === 'actual' ? 'actual' : 'monthly',
    // a bank rate is shown exactly as it was saved, never rounded
    rateText: p.rate_source === 'actual' && num(p.rate) != null ? String(num(p.rate)) : '',
  };
}

// The server re-derives a monthly rate only when the amount, date or currency changes.
const unchangedLine = (form, orig) =>
  !!orig &&
  parseAmount(form.amount) === num(orig.amount) &&
  form.purchase_date === dateOnly(orig.purchase_date) &&
  form.currency === orig.currency;

/**
 * The rate the form would save, in project-currency units per 1 unit of the
 * purchase currency: { rate, mode: 'same'|'actual'|'monthly', saved }.
 * `rows` are the purchase month's fx-rates rows (or null while loading).
 */
export function effectiveRate(form, P, rows, orig) {
  if (form.currency === P) return { rate: 1, mode: 'same' };
  if (form.rateMode === 'actual') return { rate: parseRate(form.rateText), mode: 'actual' };
  if (orig && orig.rate_source === 'monthly' && unchangedLine(form, orig)) return { rate: num(orig.rate), mode: 'monthly', saved: true };
  return { rate: rows ? monthlyRate(rows, form.currency, P) : null, mode: 'monthly' };
}

/** What is wrong with the form, in plain words; '' when it can be sent. */
export function purchaseProblem(form, P) {
  if (!isRealDate(form.purchase_date)) return 'Pick the date of the purchase.';
  if (!String(form.description || '').trim()) return 'Say what was bought.';
  if (parseAmount(form.amount) == null) return 'The amount must be a number, 0 or more, with at most 2 decimals.';
  if (!CURRENCIES.includes(form.currency)) return 'Pick the currency the supplier is paid in.';
  if (!PURCHASE_STATUSES.includes(form.status)) return 'Pick a status.';
  if (form.currency !== P && form.rateMode === 'actual' && parseRate(form.rateText) == null) {
    return `Type the bank rate (${P} per 1 ${form.currency}, above 0), or use the monthly rate.`;
  }
  return '';
}

/**
 * The POST / PUT body. `rate` is sent ONLY for a typed bank rate (the server
 * then marks it 'actual'); without it the server uses the month's rate, or 1
 * for the project currency. Turning a saved bank rate back into the monthly
 * one sends rate_source 'monthly' so the server re-derives it.
 */
export function purchasePayload(form, P, orig) {
  const body = {
    purchase_date: form.purchase_date,
    supplier: String(form.supplier || '').trim(),
    description: String(form.description || '').trim(),
    amount: parseAmount(form.amount),
    currency: form.currency,
    status: form.status,
    reference: String(form.reference || '').trim(),
  };
  if (form.currency !== P && form.rateMode === 'actual') body.rate = parseRate(form.rateText);
  else if (orig && orig.rate_source === 'actual') body.rate_source = 'monthly';
  return body;
}

/* ── display pieces ── */

const STATUS_TONE = {
  Planned: { color: '#1565C0', bg: '#E3F2FD', border: '#BBDEFB' },
  Ordered: { color: '#92400E', bg: '#FEF3C7', border: '#FDE68A' },
  Paid: { color: GREEN, bg: '#E8F5E9', border: '#C8E6C9' },
};
function StatusPill({ status }) {
  const t = STATUS_TONE[status] || { color: '#555', bg: '#F5F5F5', border: '#E0E0E0' };
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 9px',
        borderRadius: 10,
        fontSize: 10,
        fontWeight: 700,
        color: t.color,
        background: t.bg,
        border: `1px solid ${t.border}`,
      }}
    >
      {status || '-'}
    </span>
  );
}

function Tile({ label, value, color = '#0D0D0D', hint }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #DCE7D6', borderRadius: 8, padding: '10px 14px', minWidth: 0 }}>
      <div style={{ fontSize: 9, fontWeight: 800, color: '#557052', letterSpacing: 1.4, textTransform: 'uppercase', marginBottom: 4 }}>
        {label}
      </div>
      <div
        style={{
          fontSize: 16,
          fontWeight: 800,
          color,
          fontVariantNumeric: 'tabular-nums',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
        title={typeof value === 'string' ? value : undefined}
      >
        {value}
      </div>
      {hint && <div style={{ fontSize: 10, color: '#777', marginTop: 2 }}>{hint}</div>}
    </div>
  );
}

const LINE = { fontSize: 11.5, color: '#333', lineHeight: 1.55, fontVariantNumeric: 'tabular-nums' };
const LINE_LABEL = { fontWeight: 700, color: '#557052' };
const TH = {
  textAlign: 'left',
  fontSize: 9,
  fontWeight: 800,
  color: '#999',
  letterSpacing: 1,
  textTransform: 'uppercase',
  padding: '6px 8px',
  borderBottom: '1px solid #EEE',
  whiteSpace: 'nowrap',
};
const TD = { fontSize: 11.5, color: '#222', padding: '7px 8px', borderBottom: '1px solid #F2F4EE', verticalAlign: 'top' };
const NUM = { ...TD, textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' };
const LINK_BTN = {
  background: 'none',
  border: 'none',
  padding: 0,
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontSize: 11,
  fontWeight: 700,
  color: '#1565C0',
  textDecoration: 'underline',
  textUnderlineOffset: 2,
};
const SMALL_BTN = {
  background: '#fff',
  border: '1px solid #D8DED4',
  borderRadius: 6,
  padding: '4px 10px',
  fontSize: 10.5,
  fontWeight: 700,
  cursor: 'pointer',
  fontFamily: 'inherit',
};
const ERROR_BOX = {
  padding: '8px 12px',
  background: '#FFEBEE',
  border: '1px solid #FFCDD2',
  borderRadius: 8,
  color: RED,
  fontSize: 12,
  fontWeight: 600,
};

function ProfitCard({ s, P, purchases, quoteMonth }) {
  const actual = pick(s, 'actual', 'purchases_actual');
  const planned = pick(s, 'planned', 'purchases_planned');
  const profit = num(s.profit_actual);
  const profitP = num(s.profit_projected);
  const budget = num(s.budget);
  const left = num(s.budget_left);
  const foreign = purchases.filter((p) => p.currency && p.currency !== P);
  const fx = fxEffectText(s.fx_effect, P);
  const unknown = num(s.fx_unknown_lines) || 0;
  const usd = s.usd || null;
  const usdPer = usd && num(usd.usd_per_unit_of_P);
  const tone = (v) => (v == null ? '#aaa' : v >= 0 ? GREEN : RED);
  return (
    <div style={{ background: '#F3F7F1', border: '1px solid #DCE7D6', borderRadius: 10, padding: '10px 12px 12px' }}>
      <div style={{ fontSize: 9, fontWeight: 800, color: '#557052', letterSpacing: 1.6, textTransform: 'uppercase', marginBottom: 8 }}>
        Actual · costs and profit, in {P}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: 10 }}>
        <Tile label={`Revenue (${s.revenue_basis === 'PO' ? 'PO' : 'quote'})`} value={fmtMoney(s.revenue, P)} />
        <Tile label="Labor (quoted)" value={fmtMoney(s.labor, P)} color={RED} />
        <Tile label="Purchases (actual)" value={fmtMoney(actual, P)} color={RED} hint="Ordered and Paid" />
        <Tile label="Profit" value={fmtMoney(profit, P)} color={tone(profit)} />
        <Tile label="Margin" value={fmtPct(s.margin_actual)} color={tone(num(s.margin_actual))} />
      </div>
      <div style={{ marginTop: 10, display: 'grid', gap: 3 }}>
        <div style={LINE}>
          {purchases.length === 0 ? (
            <>
              <span style={LINE_LABEL}>Projected (no purchases yet, so the quoted expenses are used):</span>{' '}
            </>
          ) : (
            <>
              <span style={LINE_LABEL}>Projected (with planned purchases):</span> planned {fmtMoney(planned, P)} ·{' '}
            </>
          )}
          profit <b style={{ color: tone(profitP) }}>{fmtMoney(profitP, P)}</b> · margin {fmtPct(s.margin_projected)}
        </div>
        <div style={LINE}>
          {budget ? (
            <>
              Budget {fmtMoney(budget, P)} · spent {fmtMoney(actual, P)} · left <b style={{ color: tone(left) }}>{fmtMoney(left, P)}</b>
              {left != null && left < 0 ? ' (over budget)' : ''}
            </>
          ) : (
            <>No budget: the quote has no expenses figure · spent {fmtMoney(actual, P)}</>
          )}
        </div>
        <div style={LINE}>
          <span style={LINE_LABEL}>Exchange-rate effect since the quote{quoteMonth ? ` (${quoteMonth} rates)` : ''}:</span>{' '}
          {foreign.length === 0 ? (
            <>none, every purchase is in {P}</>
          ) : !(num(s.fx_lines) > 0) && unknown > 0 ? (
            // nothing could be measured, so do not show a "no change" zero
            <span style={{ color: '#92400E' }}>
              not known yet: no rate for {quoteMonth || 'the quote month'} (add it under Exchange rates)
            </span>
          ) : (
            <>
              <b style={{ color: fx.tone === 'loss' ? RED : fx.tone === 'gain' ? GREEN : '#333' }}>{fx.text}</b>
              {unknown > 0 && (
                <span style={{ color: '#92400E' }}>
                  {' '}
                  · {unknown} purchase{unknown === 1 ? '' : 's'} not counted: no rate for {quoteMonth || 'the quote month'}
                </span>
              )}
            </>
          )}
        </div>
        {usd &&
          P !== 'USD' &&
          (usdPer ? (
            <div style={{ ...LINE, color: '#555' }}>
              <span style={LINE_LABEL}>≈ USD:</span> revenue {fmtMoney(usd.revenue, 'USD')} · purchases {fmtMoney(usd.actual, 'USD')} ·
              profit {fmtMoney(usd.profit_actual, 'USD')} · projected profit {fmtMoney(usd.profit_projected, 'USD')}, at{' '}
              {rateText(P, usdPer, 'USD')}
            </div>
          ) : (
            <div style={{ ...LINE, color: '#92400E' }}>
              {usd.note || `No ${P} to USD rate for this month yet, so there are no USD figures.`}
            </div>
          ))}
      </div>
    </div>
  );
}

export default function KsaCostsPanel({ project, editable }) {
  const base = useApiBase();
  const id = project && project.id;
  const self = useId(); // this panel, so it skips its own change announcements
  const [data, setData] = useState(null); // { purchases, summary }
  const [loadError, setLoadError] = useState('');
  const [form, setForm] = useState(null); // the add / edit form, or null
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirmId, setConfirmId] = useState(null);
  const [rowError, setRowError] = useState('');
  const [rates, setRates] = useState({}); // 'YYYY-MM' -> { rows } | { error }
  const asked = useRef(new Set());

  const url = `${base}/projects/${encodeURIComponent(id || '')}/purchases`;
  // a slow answer for the record shown before must never land on this one
  const shown = useRef(url);
  shown.current = url;
  // ...nor an older answer for this one over a newer (a live re-read still
  // out when a save's own answer lands)
  const [reads] = useState(latestReads);
  // `quiet` (a live re-read): a failure keeps what is shown, error or not
  const load = useCallback(
    async (quiet) => {
      if (!id) return;
      const t = reads.start();
      try {
        const body = await ksaCall(url, undefined, 'Could not load the purchases');
        if (shown.current !== url || !reads.show(t)) return;
        setData({ purchases: (body && body.purchases) || [], summary: (body && body.summary) || {} });
        setLoadError('');
      } catch (e) {
        if (shown.current === url && quiet !== true) setLoadError(e.message);
      }
    },
    [id, url, reads]
  );
  // another record: nothing of the last one stays on screen
  useEffect(() => {
    setData(null);
    setForm(null);
    setLoadError('');
    setConfirmId(null);
  }, [url]);

  // re-read when the project's own money fields change (an edit in the editor)
  const q = project || {};
  useEffect(() => {
    load();
  }, [
    load,
    q.poValue,
    q.po_value,
    q.quotedPrice,
    q.quoted_price,
    q.quotedLaborCost,
    q.quoted_labor_cost,
    q.quotedExpenses,
    q.quoted_expenses,
    q.quoteDate,
    q.quote_date,
    q.currency,
  ]);
  // ...and when a rate changes in the Exchange rates panel
  useEffect(() => {
    const on = (e) => {
      if (e && e.detail && e.detail.from === self) return;
      asked.current = new Set();
      setRates({});
      load();
    };
    window.addEventListener(KSA_COSTS_CHANGED, on);
    return () => window.removeEventListener(KSA_COSTS_CHANGED, on);
  }, [load, self]);
  // ...and when someone else changes this record's purchases or the record
  // itself, or an exchange rate (src/live). Its notes and files are not money.
  const liveMatch = useMemo(() => {
    const record = matchRecord(base, id);
    const rates = matchKind(base, 'fx-rates');
    return (e) => rates(e) || (record(e) && ['', 'purchases', null].includes(recordPart(e, base, id)));
  }, [base, id]);
  const reloadQuietly = useCallback(() => load(true), [load]);
  useLiveRefresh(null, reloadQuietly, { match: liveMatch });

  const summary = (data && data.summary) || {};
  const purchases = (data && data.purchases) || [];
  const P = fxCode(summary.currency || summary.project_currency || q.currency) || 'SAR';
  const quoteMonth = monthLabel(q.quoteDate || q.quote_date);

  // the purchase month's rates, for the form's rate box and preview
  const month = form && isRealDate(form.purchase_date) ? monthOf(form.purchase_date) : '';
  useEffect(() => {
    if (!month || asked.current.has(month)) return;
    asked.current.add(month);
    ksaCall(`${base}/fx-rates?month=${month}`, undefined, `Could not load the ${monthLabel(month)} rates`)
      .then((body) => setRates((r) => ({ ...r, [month]: { rows: (body && body.rows) || [] } })))
      .catch((e) => {
        asked.current.delete(month); // a failed lookup is asked again next time
        setRates((r) => ({ ...r, [month]: { error: e.message } }));
      });
  }, [month, base]);

  const apply = (body) => {
    // a save can make the server fetch a month's rates: read them fresh next time
    asked.current = new Set();
    setRates({});
    // the save's own answer is the newest there is (latestReads)
    if (body && Array.isArray(body.purchases)) {
      if (reads.show(reads.start())) setData({ purchases: body.purchases, summary: body.summary || {} });
    } else load();
    notifyKsaCostsChanged(self);
  };

  const orig = form && form.id ? purchases.find((p) => p.id === form.id) : null;
  const save = async () => {
    if (!form || saving) return;
    const problem = purchaseProblem(form, P);
    if (problem) {
      setFormError(problem);
      return;
    }
    setSaving(true);
    setFormError('');
    try {
      const body = await ksaCall(
        form.id ? `${url}/${encodeURIComponent(form.id)}` : url,
        {
          method: form.id ? 'PUT' : 'POST',
          headers: JSON_HEADERS,
          body: JSON.stringify(purchasePayload(form, P, orig)),
        },
        'Could not save the purchase'
      );
      apply(body);
      setForm(null);
    } catch (e) {
      setFormError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (pid) => {
    setConfirmId(null);
    setRowError('');
    try {
      apply(await ksaCall(`${url}/${encodeURIComponent(pid)}`, { method: 'DELETE' }, 'Could not delete the purchase'));
    } catch (e) {
      setRowError(e.message);
    }
  };

  const openForm = (p) => {
    setForm(purchaseForm(p));
    setFormError('');
    setRowError('');
  };
  const setField = (k) => (e) => {
    const v = e.target.value;
    // a bank rate belongs to one currency: a new currency starts from its monthly rate
    setForm((f) => (k === 'currency' && v !== f.currency ? { ...f, currency: v, rateMode: 'monthly', rateText: '' } : { ...f, [k]: v }));
  };

  if (!data) {
    return loadError ? (
      <div role="alert" style={{ ...ERROR_BOX, marginTop: 12, display: 'flex', alignItems: 'center', gap: 12 }}>
        <span>{loadError}</span>
        <button type="button" style={SMALL_BTN} onClick={load}>
          Try again
        </button>
      </div>
    ) : (
      <div style={{ marginTop: 12, fontSize: 11.5, color: '#aaa' }}>Loading costs and profit...</div>
    );
  }

  /* the add / edit form */
  let formBlock = null;
  if (form) {
    const monthRates = month ? rates[month] : null;
    const eff = effectiveRate(form, P, monthRates && monthRates.rows, orig);
    const amount = parseAmount(form.amount);
    const preview = amount != null && eff.rate ? convertAmount(amount, eff.rate) : null;
    const mLabel = monthLabel(form.purchase_date);
    let rateNote = null;
    if (eff.mode === 'monthly' && !eff.rate) {
      rateNote = !month ? null : !monthRates ? (
        <span style={{ color: '#888' }}>Loading the {mLabel} rates...</span>
      ) : monthRates.error ? (
        <span style={{ color: RED }}>{monthRates.error}. Type the bank rate, or save and the server will look the rate up.</span>
      ) : (
        <span style={{ color: '#92400E' }}>
          No {form.currency} rate for {mLabel} in the rate table yet. Save to fetch it from the ECB, or type the bank rate.
        </span>
      );
    }
    formBlock = (
      <div style={{ background: '#fff', border: '1px solid #C8E6C9', borderRadius: 10, padding: '14px 16px', marginBottom: 12 }}>
        <div style={{ fontSize: 11, fontWeight: 800, color: GREEN, letterSpacing: 1.4, textTransform: 'uppercase', marginBottom: 10 }}>
          {form.id ? 'Edit purchase' : 'Add a purchase'}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 10 }}>
          <label>
            <span style={LABEL}>Date</span>
            <input type="date" style={INPUT} value={form.purchase_date} onChange={setField('purchase_date')} aria-label="Purchase date" />
          </label>
          <label>
            <span style={LABEL}>Supplier</span>
            <input style={INPUT} value={form.supplier} onChange={setField('supplier')} aria-label="Supplier" />
          </label>
          <label style={{ gridColumn: 'span 2' }}>
            <span style={LABEL}>
              What was bought <span style={{ color: RED }}>*</span>
            </span>
            <input style={INPUT} value={form.description} onChange={setField('description')} aria-label="What was bought" />
          </label>
          <label>
            <span style={LABEL}>Amount</span>
            <input
              style={{ ...INPUT, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}
              value={form.amount}
              inputMode="decimal"
              onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value.replace(/[^0-9.,]/g, '') }))}
              placeholder="0.00"
              aria-label="Amount"
            />
          </label>
          <label>
            <span style={LABEL}>Currency (paid in)</span>
            <select style={INPUT} value={form.currency} onChange={setField('currency')} aria-label="Currency">
              {CURRENCIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span style={LABEL}>Status</span>
            <select style={INPUT} value={form.status} onChange={setField('status')} aria-label="Status">
              {PURCHASE_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span style={LABEL}>Reference (supplier PO / invoice #)</span>
            <input style={INPUT} value={form.reference} onChange={setField('reference')} aria-label="Reference" />
          </label>
        </div>
        <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 12, color: '#333' }}>
          <span style={{ fontWeight: 700, color: '#555' }}>Rate:</span>
          {eff.mode === 'same' ? (
            <span>{P} is the project currency, so no conversion (rate 1).</span>
          ) : (
            <>
              <span>1 {form.currency} =</span>
              <input
                style={{ ...INPUT, width: 130, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}
                value={eff.mode === 'actual' ? form.rateText : fmtRateExact(eff.rate)}
                title={eff.rate ? `${fmtRateFull(eff.rate)} ${P} per 1 ${form.currency}` : undefined}
                onChange={(e) => {
                  const v = e.target.value.replace(/[^0-9.,]/g, '');
                  setForm((f) => ({ ...f, rateMode: 'actual', rateText: v }));
                }}
                placeholder="type the bank rate"
                inputMode="decimal"
                aria-label={`${P} per 1 ${form.currency}`}
              />
              <span>{P}</span>
              {eff.mode === 'actual' ? (
                <>
                  <span style={{ fontWeight: 700, color: '#6A1B9A' }}>· bank rate</span>
                  <button type="button" style={LINK_BTN} onClick={() => setForm((f) => ({ ...f, rateMode: 'monthly', rateText: '' }))}>
                    use the monthly rate
                  </button>
                </>
              ) : eff.rate ? (
                <span style={{ color: '#557052', fontWeight: 600 }}>
                  · {eff.saved ? `saved ${mLabel} rate` : `${mLabel} rate`} (type over it to use the bank rate)
                </span>
              ) : null}
            </>
          )}
        </div>
        {rateNote && <div style={{ marginTop: 4, fontSize: 11.5 }}>{rateNote}</div>}
        <div style={{ marginTop: 8, fontSize: 12.5, color: '#0D0D0D', fontVariantNumeric: 'tabular-nums' }}>
          In {P}: <b>{preview == null ? '-' : fmtMoney(preview, P)}</b>
          {preview != null && eff.mode !== 'same' && (
            <span style={{ color: '#777' }}>
              {' '}
              ({fmtMoney(amount, form.currency)} × {fmtRateFull(eff.rate)})
            </span>
          )}
        </div>
        <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={save}
            disabled={saving}
            style={{ ...BTN_PRIMARY, opacity: saving ? 0.6 : 1, cursor: saving ? 'wait' : 'pointer' }}
          >
            {saving ? 'Saving...' : form.id ? 'Save changes' : 'Add purchase'}
          </button>
          <button
            type="button"
            onClick={() => {
              setForm(null);
              setFormError('');
            }}
            style={{ ...SMALL_BTN, padding: '8px 16px', fontSize: 11 }}
          >
            Cancel
          </button>
          {formError && (
            <span role="alert" style={{ color: RED, fontSize: 12, fontWeight: 600 }}>
              {formError}
            </span>
          )}
        </div>
      </div>
    );
  }

  const planned = pick(summary, 'planned', 'purchases_planned');
  const actual = pick(summary, 'actual', 'purchases_actual');
  return (
    <div style={{ marginTop: 12 }} aria-label="Costs and profit">
      {loadError && (
        <div role="alert" style={{ ...ERROR_BOX, marginBottom: 8 }}>
          {loadError}
        </div>
      )}
      {summary.error ? (
        // the record's currency is not one the server can convert (fix it in the record)
        <div role="alert" style={ERROR_BOX}>
          {summary.error}
        </div>
      ) : (
        <ProfitCard s={summary} P={P} purchases={purchases} quoteMonth={quoteMonth} />
      )}

      <div style={{ marginTop: 12, background: '#fff', border: '1px solid #E5E9DD', borderRadius: 10, padding: '12px 14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 8 }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: GREEN, letterSpacing: 1.6, textTransform: 'uppercase' }}>
            Purchases{purchases.length ? ` (${purchases.length})` : ''}
          </div>
          {editable && !form && (
            <button type="button" onClick={() => openForm(null)} style={{ ...BTN_PRIMARY, padding: '7px 14px' }}>
              + Add purchase
            </button>
          )}
        </div>
        {formBlock}
        {rowError && (
          <div role="alert" style={{ ...ERROR_BOX, marginBottom: 8 }}>
            {rowError}
          </div>
        )}
        {purchases.length === 0 ? (
          <div style={{ fontSize: 11.5, color: '#999', padding: '10px 0' }}>
            No purchases yet. Add what is bought for this project, in the currency the supplier is paid in.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {['Date', 'Supplier', 'What', 'Amount', 'Rate', `In ${P}`, 'Status', 'Ref'].map((h) => (
                    <th key={h} style={['Amount', `In ${P}`].includes(h) ? { ...TH, textAlign: 'right' } : TH}>
                      {h}
                    </th>
                  ))}
                  {editable && <th style={TH} aria-label="Actions" />}
                </tr>
              </thead>
              <tbody>
                {purchases.map((p) => (
                  <tr key={p.id}>
                    <td style={{ ...TD, whiteSpace: 'nowrap' }}>{fmtDate(p.purchase_date) || '-'}</td>
                    <td style={TD}>{p.supplier || '-'}</td>
                    <td style={TD}>{p.description}</td>
                    <td style={NUM}>{fmtMoney(p.amount, p.currency)}</td>
                    <td
                      style={{ ...TD, whiteSpace: 'nowrap', color: '#444' }}
                      title={num(p.rate) != null ? `Exact rate: 1 ${p.currency} = ${fmtRateFull(p.rate)} ${P}` : undefined}
                    >
                      {p.rate_source === 'same' || p.currency === P ? (
                        'Same currency'
                      ) : (
                        <>
                          {rateText(p.currency, p.rate, P)}{' '}
                          <span style={{ color: p.rate_source === 'actual' ? '#6A1B9A' : '#888', fontWeight: 600 }}>
                            · {purchaseRateSource(p)}
                          </span>
                        </>
                      )}
                    </td>
                    <td style={{ ...NUM, fontWeight: 700 }}>
                      {fmtMoney(p.amount_in_project_currency ?? convertAmount(p.amount, p.rate), P)}
                    </td>
                    <td style={TD}>
                      <StatusPill status={p.status} />
                    </td>
                    <td style={TD}>{p.reference || '-'}</td>
                    {editable && (
                      <td style={{ ...TD, whiteSpace: 'nowrap', textAlign: 'right' }}>
                        {confirmId === p.id ? (
                          <>
                            <span style={{ fontSize: 11, color: RED, fontWeight: 700, marginRight: 6 }}>Delete this purchase?</span>
                            <button
                              type="button"
                              style={{ ...SMALL_BTN, color: '#fff', background: RED, border: `1px solid ${RED}` }}
                              onClick={() => remove(p.id)}
                            >
                              Yes, delete
                            </button>{' '}
                            <button type="button" style={SMALL_BTN} onClick={() => setConfirmId(null)}>
                              No
                            </button>
                          </>
                        ) : (
                          <>
                            <button type="button" style={SMALL_BTN} onClick={() => openForm(p)} disabled={!!form}>
                              Edit
                            </button>{' '}
                            <button
                              type="button"
                              style={{ ...SMALL_BTN, color: RED, border: '1px solid #FFCDD2' }}
                              onClick={() => setConfirmId(p.id)}
                            >
                              Delete
                            </button>
                          </>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ marginTop: 8, fontSize: 11.5, color: '#555', fontVariantNumeric: 'tabular-nums' }}>
              Actual (Ordered and Paid): <b>{fmtMoney(actual, P)}</b> · Planned: <b>{fmtMoney(planned, P)}</b>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
